import { atom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';

/**
 * Authentication atoms for Custodian Portal
 *
 * SECURITY: JWT tokens are stored in httpOnly cookies (not accessible via JavaScript)
 * This prevents XSS attacks from stealing authentication tokens.
 *
 * Session information is stored in sessionStorage and populated from the login response.
 * The actual JWT token remains secure in the httpOnly cookie - we only store
 * non-sensitive session metadata (userId, email, etc.) for UI purposes.
 * sessionStorage is cleared when the browser tab closes, limiting PII exposure (GDPR data minimisation).
 */

// Session information stored in sessionStorage (populated from login response)
// Non-sensitive metadata only - the JWT token is in httpOnly cookie
export interface SessionData {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  role: 'Custodian' | 'SendingJurisdiction';
  organizationId: string;
  organizationName: string;
  preferredLanguage: string;
  mfaEnabled: boolean;
  expiresAt: string | null; // ISO string for JSON serialization
  agentDelegation?: {
    isDelegatedSession: boolean;
    agentId: string;
    agentName: string;
    agentRole: string;
    entityType: string;
    isReadOnly: boolean;
    restrictProfileChanges: boolean;
  };
}

// Session atom with sessionStorage persistence (GDPR data minimisation — cleared on tab close)
// getOnInit: true ensures the stored value is available on first render (prevents redirect flash on refresh)
const sessionDataAtom = atomWithStorage<SessionData | null>(
  'portalSession',
  null,
  createJSONStorage(() => sessionStorage),
  { getOnInit: true }
);
sessionDataAtom.debugLabel = 'sessionDataAtom';

// Action atom to set session data from login response
export const setSessionAtom = atom(null, (_get, set, session: SessionData | null) => {
  set(sessionDataAtom, session);
});
setSessionAtom.debugLabel = 'setSessionAtom';

// User information atoms - derived from session data
export const userIdAtom = atom<string>((get) => {
  const session = get(sessionDataAtom);
  return session?.userId || '';
});
userIdAtom.debugLabel = 'userIdAtom';

export const userEmailAtom = atom<string>((get) => {
  const session = get(sessionDataAtom);
  return session?.email || '';
});
userEmailAtom.debugLabel = 'userEmailAtom';

export const userFullNameAtom = atom<string>((get) => {
  const session = get(sessionDataAtom);
  return session?.fullName || '';
});
userFullNameAtom.debugLabel = 'userFullNameAtom';

export const userFirstNameAtom = atom<string>((get) => {
  const session = get(sessionDataAtom);
  return session?.firstName || '';
});
userFirstNameAtom.debugLabel = 'userFirstNameAtom';

export const userLastNameAtom = atom<string>((get) => {
  const session = get(sessionDataAtom);
  return session?.lastName || '';
});
userLastNameAtom.debugLabel = 'userLastNameAtom';

// RBAC atoms
export const userRoleAtom = atom<'Custodian' | 'SendingJurisdiction' | ''>((get) => {
  const session = get(sessionDataAtom);
  return session?.role || '';
});
userRoleAtom.debugLabel = 'userRoleAtom';

export const organizationIdAtom = atom<string>((get) => {
  const session = get(sessionDataAtom);
  return session?.organizationId || '';
});
organizationIdAtom.debugLabel = 'organizationIdAtom';

export const organizationNameAtom = atom<string>((get) => {
  const session = get(sessionDataAtom);
  return session?.organizationName || '';
});
organizationNameAtom.debugLabel = 'organizationNameAtom';

export const preferredLanguageAtom = atom<string>((get) => {
  const session = get(sessionDataAtom);
  return session?.preferredLanguage || 'en';
});
preferredLanguageAtom.debugLabel = 'preferredLanguageAtom';

export const mfaEnabledAtom = atom<boolean>((get) => {
  const session = get(sessionDataAtom);
  return session?.mfaEnabled || false;
});
mfaEnabledAtom.debugLabel = 'mfaEnabledAtom';

export const isDelegatedSessionAtom = atom<boolean>((get) => {
  const session = get(sessionDataAtom);
  return session?.agentDelegation?.isDelegatedSession ?? false;
});
isDelegatedSessionAtom.debugLabel = 'isDelegatedSessionAtom';

export const agentDelegationAtom = atom((get) => {
  const session = get(sessionDataAtom);
  return session?.agentDelegation ?? null;
});
agentDelegationAtom.debugLabel = 'agentDelegationAtom';

// Session expiration - derived from session data (converts ISO string to Date)
export const sessionExpiresAtAtom = atom<Date | null>((get) => {
  const session = get(sessionDataAtom);
  if (!session?.expiresAt) return null;
  return new Date(session.expiresAt);
});
sessionExpiresAtAtom.debugLabel = 'sessionExpiresAtAtom';

// Derived atom - is user authenticated (has valid session in sessionStorage)
export const isAuthenticatedAtom = atom((get) => {
  const session = get(sessionDataAtom);
  const expiresAt = get(sessionExpiresAtAtom);

  if (!session || !expiresAt) {
    return false;
  }

  // Check if session is still valid
  return new Date() < expiresAt;
});
isAuthenticatedAtom.debugLabel = 'isAuthenticatedAtom';

// MFA verification state (for two-step login)
export const mfaRequiredAtom = atom<boolean>(false);
mfaRequiredAtom.debugLabel = 'mfaRequiredAtom';

export const mfaUserIdAtom = atom<string | null>(null);
mfaUserIdAtom.debugLabel = 'mfaUserIdAtom';

export const mfaTokenAtom = atom<string | null>(null);
mfaTokenAtom.debugLabel = 'mfaTokenAtom';

// Legacy alias for backward compatibility
export const mfaPartialTokenAtom = mfaTokenAtom;

// Password reset required state
export const requiresPasswordResetAtom = atom<boolean>(false);
requiresPasswordResetAtom.debugLabel = 'requiresPasswordResetAtom';

// Loading state
export const authLoadingAtom = atom<boolean>(true);
authLoadingAtom.debugLabel = 'authLoadingAtom';

// Error state
export const authErrorAtom = atom<string | null>(null);
authErrorAtom.debugLabel = 'authErrorAtom';

// Action atom to update MFA enabled status in session
export const updateMfaStatusAtom = atom(null, (get, set, mfaEnabled: boolean) => {
  const session = get(sessionDataAtom);
  if (session) {
    set(sessionDataAtom, { ...session, mfaEnabled });
  }
});
updateMfaStatusAtom.debugLabel = 'updateMfaStatusAtom';
